import { Stack, usePathname, router } from 'expo-router';
import { PaperProvider, MD3LightTheme, MD3DarkTheme } from 'react-native-paper';
import { useColorScheme, StatusBar, Platform, Animated, AppState } from 'react-native';
import { I18nextProvider } from 'react-i18next';
import i18n, { initializeI18n } from '@/src/lib/i18n';
import { useEffect, useState, useRef } from 'react';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { OnboardingProvider } from '@/src/context/OnboardingContext';
import { useFonts } from 'expo-font';
import * as SplashScreen from 'expo-splash-screen';
import { StripeProvider } from '@/src/providers/StripeProvider';
import { AuthProvider } from '@/src/context/AuthContext';
import { CompanyProvider } from '@/src/context/CompanyContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Linking from 'expo-linking';
import * as Sentry from '@sentry/react-native';
import { LoadingScreen } from '@/src/components/LoadingScreen';
import { ErrorBoundary } from '@/src/components/ErrorBoundary';
import { ANALYTICS_DISABLED, initAnalytics, registerSuperProperties, track, flush as flushAnalytics, captureAttributionParams } from '@/src/lib/analytics';
import { ConsentBanner } from '@/components/ConsentBanner';
import { PreviewBanner } from '@/components/PreviewBanner';
import { getStoredConsent, subscribeToConsentChanges } from '@/services/consent';
import type { ConsentSettings } from '@/services/consent';
import { applySentryConsent } from '@/src/lib/sentryInit';
import { parseDeepLink, resolveDeepLinkTarget } from '@/src/utils/deepLinkRouter';
import sentryUtils from '@/src/utils/sentry';
import {
  SENTRY_RELEASE,
  APP_BUILD_VERSION,
  APP_VERSION,
  APP_VARIANT,
  GIT_SHA,
  RUNTIME_VERSION,
  OTA_UPDATE_ID,
} from '@/src/lib/buildInfo';

// Background-location task registration moved to frontend/index.js (the JS
// entry). It MUST run before expo-router so headless JobService relaunches —
// which never evaluate route modules — still define the task. Do not
// re-register here: see the comment in index.js.

// Sentry.init and the build/release identifiers moved out of this route module.
//   - Identifiers: src/lib/buildInfo.ts (imported above; still used for the
//     analytics super-properties and Sentry tags set in RootLayout below).
//   - Sentry.init: src/lib/sentryInit.ts, invoked from index.js so the native
//     crash handler is armed even on headless background-location JobService
//     relaunches, which never evaluate this file. That blind spot hid the top
//     production crash (TaskService.executeTask NPE). See sentryInit.ts.

// Import global CSS for web platform and preload fonts
if (Platform.OS === 'web') {
  require('@/src/styles/global.css');
}

const canUseDOM = Platform.OS === 'web' && typeof document !== 'undefined';

const APP_TITLE = 'AllDrivers';

const routeTitleMap: Record<string, string> = {
  '/': 'Login',
  '/register/choice': 'Register',
  '/register/driver': 'Register Driver',
  '/register/owner': 'Register Owner',
  '/register/success': 'Registration Complete',
  '/reset-password/confirm': 'Reset Password',
  '/dashboard/home': 'Dashboard',
  '/dashboard/report': 'Reports',
  '/dashboard/daily-report': 'Daily Report',
  '/dashboard/drivers': 'Drivers',
  '/dashboard/vehicles': 'Vehicles',
  '/dashboard/gps': 'GPS',
  '/dashboard/invitations': 'Invitations',
  '/dashboard/manage-licenses': 'Manage Licenses',
  '/dashboard/settings': 'Settings',
  '/dashboard/user-data': 'User Data',
  '/dashboard/company': 'Company',
  '/dashboard/my-vehicles': 'My Vehicles',
  '/dashboard/drivers-location': 'Drivers Location',
  '/download': 'Download',
  '/privacy': 'Privacy',
};

const formatRouteTitle = (pathname: string) => {
  const normalized = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
  if (routeTitleMap[normalized]) {
    return `${routeTitleMap[normalized]} | ${APP_TITLE}`;
  }

  const segments = normalized.split('/').filter(Boolean);
  if (segments.length === 0) {
    return APP_TITLE;
  }

  const title = segments
    .map((segment) => segment.replace(/[-_]/g, ' '))
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' · ');

  return `${title} | ${APP_TITLE}`;
};

const preloadWebFonts = () => {
  if (!canUseDOM) return;

  const materialIconsLink = document.createElement('link');
  materialIconsLink.rel = 'preload';
  materialIconsLink.as = 'style';
  materialIconsLink.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
  document.head.appendChild(materialIconsLink);

  const materialCommunityIconsLink = document.createElement('link');
  materialCommunityIconsLink.rel = 'preload';
  materialCommunityIconsLink.as = 'style';
  materialCommunityIconsLink.href = 'https://cdn.jsdelivr.net/npm/@mdi/font@7.2.96/css/materialdesignicons.min.css';
  document.head.appendChild(materialCommunityIconsLink);

  // Also add as stylesheet for actual loading
  const styleLink1 = document.createElement('link');
  styleLink1.rel = 'stylesheet';
  styleLink1.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
  document.head.appendChild(styleLink1);

  const styleLink2 = document.createElement('link');
  styleLink2.rel = 'stylesheet';
  styleLink2.href = 'https://cdn.jsdelivr.net/npm/@mdi/font@7.2.96/css/materialdesignicons.min.css';
  document.head.appendChild(styleLink2);
};

// Icons object with web specific configuration
const webIcons = Platform.OS === 'web' ? {
  handlebar: 'mdi-menu',
  dropdown: 'mdi-chevron-down',
  close: 'mdi-close',
  plus: 'mdi-plus',
  checkmark: 'mdi-check',
  delete: 'mdi-delete',
  cancel: 'mdi-cancel',
  warning: 'mdi-alert',
  eye: 'mdi-eye',
  'eye-off': 'mdi-eye-off',
} : undefined;

// Custom light theme
const customLightTheme = {
  ...MD3LightTheme,
  roundness: 8,
  ...(Platform.OS === 'web' ? { icons: webIcons } : {}),
  colors: {
    ...MD3LightTheme.colors,
    primary: "#017CC7",
    onPrimary: "rgb(255, 255, 255)",
    primaryContainer: "rgb(219, 225, 255)",
    onPrimaryContainer: "rgb(0, 23, 75)",
    secondary: "rgb(89, 94, 114)",
    onSecondary: "rgb(255, 255, 255)",
    secondaryContainer: "rgb(221, 225, 249)",
    onSecondaryContainer: "rgb(22, 27, 44)",
    tertiary: "rgb(0, 95, 175)",
    onTertiary: "rgb(255, 255, 255)",
    tertiaryContainer: "rgb(212, 227, 255)",
    onTertiaryContainer: "rgb(0, 28, 58)",
    error: "rgb(186, 26, 26)",
    onError: "rgb(255, 255, 255)",
    errorContainer: "rgb(255, 218, 214)",
    onErrorContainer: "rgb(65, 0, 2)",
    background: "rgb(254, 251, 255)",
    onBackground: "rgb(27, 27, 31)",
    surface: "rgb(254, 251, 255)",
    onSurface: "rgb(27, 27, 31)",
    surfaceVariant: "rgb(226, 226, 236)",
    onSurfaceVariant: "rgb(69, 70, 79)",
    outline: "rgb(117, 118, 128)",
    outlineVariant: "rgb(197, 198, 208)",
    scrim: "rgb(0, 0, 0)",
    inverseSurface: "rgb(48, 48, 52)",
    inverseOnSurface: "rgb(242, 240, 244)",
    inversePrimary: "rgb(180, 197, 255)",
    elevation: {
      level0: "transparent",
      level1: "rgb(241, 243, 253)",
      level2: "rgb(234, 238, 252)",
      level3: "rgb(226, 233, 251)",
      level4: "rgb(224, 231, 251)",
      level5: "rgb(218, 228, 250)",
    },
    surfaceDisabled: "rgba(27, 27, 31, 0.12)",
    onSurfaceDisabled: "rgba(27, 27, 31, 0.38)",
    backdrop: "rgba(46, 48, 56, 0.4)",
  },
};

// Custom dark theme
const customDarkTheme = {
  ...MD3DarkTheme,
  roundness: 8,
  ...(Platform.OS === 'web' ? { icons: webIcons } : {}),
  colors: {
    ...MD3DarkTheme.colors,
    primary: "#00B4E4",
    onPrimary: "rgb(255, 255, 255)",
    primaryContainer: "rgb(0, 62, 168)",
    onPrimaryContainer: "rgb(219, 225, 255)",
    secondary: "rgb(193, 197, 221)",
    onSecondary: "rgb(43, 48, 66)",
    secondaryContainer: "rgb(65, 70, 89)",
    onSecondaryContainer: "rgb(221, 225, 249)",
    tertiary: "#0069AA",
    onTertiary: "#FFFFFF",
    tertiaryContainer: "rgb(0, 71, 134)",
    onTertiaryContainer: "rgb(212, 227, 255)",
    error: "rgb(255, 180, 171)",
    onError: "rgb(105, 0, 5)",
    errorContainer: "rgb(147, 0, 10)",
    onErrorContainer: "rgb(255, 180, 171)",
    background: "rgb(27, 27, 31)",
    onBackground: "rgb(228, 226, 230)",
    surface: "rgb(27, 27, 31)",
    onSurface: "rgb(228, 226, 230)",
    surfaceVariant: "rgb(69, 70, 79)",
    onSurfaceVariant: "rgb(197, 198, 208)",
    outline: "rgb(143, 144, 154)",
    outlineVariant: "rgb(69, 70, 79)",
    scrim: "rgb(0, 0, 0)",
    inverseSurface: "rgb(228, 226, 230)",
    inverseOnSurface: "rgb(48, 48, 52)",
    inversePrimary: "rgb(0, 83, 219)",
    elevation: {
      level0: "transparent",
      level1: "rgb(35, 36, 42)",
      level2: "rgb(39, 41, 49)",
      level3: "rgb(44, 46, 56)",
      level4: "rgb(45, 47, 58)",
      level5: "rgb(48, 51, 62)",
    },
    surfaceDisabled: "rgba(228, 226, 230, 0.12)",
    onSurfaceDisabled: "rgba(228, 226, 230, 0.38)",
    backdrop: "rgba(46, 48, 56, 0.4)",
  },
};

const queryClient = new QueryClient();

// Keep the splash screen visible while we fetch resources
SplashScreen.preventAutoHideAsync();

export default Sentry.wrap(function RootLayout() {
  // Use expo-font's useFonts hook for mobile (fonts are pre-loaded in bundle)
  // On web, fonts are loaded via CSS links in the HTML head
  const [fontsLoadedFromHook] = useFonts(
    Platform.OS === 'web' 
      ? {} // Empty on web since fonts load via CSS
      : {
          MaterialCommunityIcons: require('@expo/vector-icons/build/vendor/react-native-vector-icons/Fonts/MaterialCommunityIcons.ttf'),
        }
  );
  const pathname = usePathname();
  const formattedTitle = formatRouteTitle(pathname || '/');
  
  // On web, fonts are available via CSS, so consider them loaded immediately
  // On mobile, wait for useFonts to complete
  const fontsLoaded = Platform.OS === 'web' ? true : fontsLoadedFromHook;

  useEffect(() => {
    if (!canUseDOM) return;
    document.title = formattedTitle;
  }, [formattedTitle]);

  useEffect(() => {
    let mounted = true;
    getStoredConsent().then((stored) => {
      if (!mounted) return;
      setConsent(stored);
      void applySentryConsent(stored);
    });
    const unsubscribe = subscribeToConsentChanges((next) => {
      setConsent(next);
      void applySentryConsent(next);
    });
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const path = pathname || '/';
    track('page_view', {
      path,
      title: formattedTitle,
      platform: Platform.OS,
    });
    track('Page View', {
      page_url: path,
      page_title: formattedTitle,
    });
  }, [formattedTitle, pathname]);

  const [isI18nInitialized, setIsI18nInitialized] = useState(false);
  const [appReady, setAppReady] = useState(false);
  const [consent, setConsent] = useState<ConsentSettings | null>(null);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const colorScheme = useColorScheme();
  const currentTheme = colorScheme === 'dark' ? customDarkTheme : customLightTheme;

  // Preload fonts in web for faster icon rendering (client only)
  useEffect(() => {
    if (!canUseDOM) return;

    const handleReady = () => {
      preloadWebFonts();
      document.removeEventListener('DOMContentLoaded', handleReady);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', handleReady);
      return () => document.removeEventListener('DOMContentLoaded', handleReady);
    }

    preloadWebFonts();
  }, []);

  // Inject Mixpanel script on web only after analytics consent.
  useEffect(() => {
    if (!canUseDOM) return;
    if (!consent?.analytics) {
      (window as any).mixpanel?.opt_out_tracking?.();
      (window as any).mixpanel?.stop_session_recording?.();
      return;
    }

    if (document.getElementById('mixpanel-init')) return;

    // Token from env only (#91): no hardcoded prod fallback. If unset, we do
    // NOT inject the Mixpanel script, so dev/staging builds never send events
    // to the production project. `disabled` is the same «no token» statement
    // written in a value eas.json accepts — an empty string is rejected there
    // and took every eas command down with it (#301). Without this check a dev
    // build would inject `mixpanel.init('disabled')`.
    const token = process.env.EXPO_PUBLIC_MIXPANEL_TOKEN;
    if (!token || token === ANALYTICS_DISABLED) {
      return;
    }
    const script = document.createElement('script');
    script.id = 'mixpanel-init';
    script.type = 'text/javascript';
    script.innerHTML = `(function(e,c){if(!c.__SV){var l,h;window.mixpanel=c;c._i=[];c.init=function(q,r,f){function t(d,a){var g=a.split(".");2==g.length&&(d=d[g[0]],a=g[1]);d[a]=function(){d.push([a].concat(Array.prototype.slice.call(arguments,0)))}}var b=c;"undefined"!==typeof f?b=c[f]=[]:f="mixpanel";b.people=b.people||[];b.toString=function(d){var a="mixpanel";"mixpanel"!==f&&(a+="."+f);d(a+=" (stub)");return a};b.people.toString=function(){return b.toString(1)+".people (stub)"};l="disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders start_session_recording stop_session_recording people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" "); for(h=0;h<l.length;h++)t(b,l[h]);var n="set set_once union unset remove delete".split(" ");b.get_group=function(){function d(p){a[p]=function(){b.push([g,[p].concat(Array.prototype.slice.call(arguments,0))])}}for(var a={},g=["get_group"].concat(Array.prototype.slice.call(arguments,0)),m=0;m<n.length;m++)d(n[m]);return a};c._i.push([q,r,f])};c.__SV=1.2;var k=e.createElement("script");k.type="text/javascript";k.async=!0;k.src="undefined"!==typeof MIXPANEL_CUSTOM_LIB_URL?MIXPANEL_CUSTOM_LIB_URL:"file:"=== e.location.protocol&&"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js".match(/^\\/\\//)?"https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js":"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";e=e.getElementsByTagName("script")[0];e.parentNode.insertBefore(k,e)}})(document,window.mixpanel||[]); mixpanel.init('${token}', { debug: true, track_pageview: false, persistence: "localStorage", autocapture: false, record_sessions_percent: 0, api_host: "https://api-eu.mixpanel.com" });`;
    document.head.appendChild(script);
  }, [consent?.analytics]);

  // Global deep link handler
  // Routes universal links / custom-scheme deep links on native. On web,
  // expo-router already owns the current browser URL; feeding getInitialURL()
  // back into router.replace() during hydration causes a self-navigation and a
  // React hydration mismatch on routes such as /checkout-return.
  useEffect(() => {
    if (Platform.OS === 'web') return;

    const handleDeepLink = (event: { url: string }) => {
      try {
        const parsedRoute = parseDeepLink(event.url);

        if (parsedRoute.type === 'unknown') {
          // Don't navigate and don't crash — just leave a breadcrumb so we
          // can see unrecognized/malformed links in Sentry without taking
          // the user away from wherever cold-start/foreground already put
          // them.
          sentryUtils.addBreadcrumb({
            category: 'deep-link',
            message: `Unrecognized deep link (${parsedRoute.reason})`,
            level: 'warning',
            data: { url: parsedRoute.url },
          });
          return;
        }

        const target = resolveDeepLinkTarget(parsedRoute);
        if (!target) {
          return;
        }

        sentryUtils.addBreadcrumb({
          category: 'deep-link',
          message: `Routing deep link: ${parsedRoute.type}`,
          level: 'info',
          data: { pathname: target.pathname },
        });

        router.replace(target as Parameters<typeof router.replace>[0]);
      } catch (err) {
        // Deep link handling must never crash cold-start / the app.
        sentryUtils.captureException(err, { extra: { url: event?.url ?? null } });
      }
    };

    // Check for initial URL
    const checkInitialURL = async () => {
      try {
        const initialURL = await Linking.getInitialURL();
        if (initialURL) {
          handleDeepLink({ url: initialURL });
        }
      } catch {
        // Silently ignore errors
      }
    };

    checkInitialURL();

    // Add global event listener
    const subscription = Linking.addEventListener('url', handleDeepLink);
    
    return () => {
      subscription.remove();
    };
  }, []);

  // Initialize Mixpanel and keep super properties aligned with Sentry release.
  // Also flush queued events whenever the app goes to background — without this,
  // session/driving start/end events tracked right before the user pockets the
  // phone sit in the local queue for up to 60s and get dropped if iOS suspends
  // the JS thread before the next flush tick.
  useEffect(() => {
    const syncAnalyticsContext = async () => {
      if (!consent?.analytics) return;
      await initAnalytics();
      await registerSuperProperties({
        app_release: APP_BUILD_VERSION,
        sentry_release: SENTRY_RELEASE || 'unknown',
        app_version: APP_VERSION,
        app_variant: APP_VARIANT,
        git_sha: GIT_SHA,
        runtime_version: RUNTIME_VERSION,
        ota_update_id: OTA_UPDATE_ID,
        platform: Platform.OS,
      });
      // MR2 (#100 / #90): capture deep-link / entry utm attribution and forward
      // as allowlisted super-properties so GA4 + Mixpanel segment by source without
      // any cross-device install-attribution identifier (privacy-first).
      const attribution = captureAttributionParams();
      if (Object.keys(attribution).length > 0) {
        await registerSuperProperties(attribution);
      }
    };
    void syncAnalyticsContext();

    if (Platform.OS === 'web') return;
    const sub = AppState.addEventListener('change', (nextState) => {
      if (nextState === 'background' || nextState === 'inactive') {
        flushAnalytics();
      }
    });
    return () => sub.remove();
  }, [consent?.analytics]);

  // One source of truth for running release/version in runtime logs + Sentry tags
  useEffect(() => {
    if (SENTRY_RELEASE) {
      Sentry.setTag('app.release', SENTRY_RELEASE);
    }
    Sentry.setTag('app.build_version', APP_BUILD_VERSION);

    // Mirror the Mixpanel super-properties (registered above) as Sentry tags so
    // the same keys are filterable in both tools — e.g. pivot from a Mixpanel
    // funnel anomaly on a given ota_update_id straight to its Sentry issues.
    // Names match the Mixpanel keys 1:1 on purpose. (app_variant/app_version are
    // already covered by environment + the SDK's native app context.)
    Sentry.setTag('ota_update_id', OTA_UPDATE_ID);
    Sentry.setTag('git_sha', GIT_SHA);
    Sentry.setTag('runtime_version', RUNTIME_VERSION);

    if (__DEV__) {
      console.info('[build-version]', APP_BUILD_VERSION);
    }
  }, []);

  // Initialize i18n on mount
  useEffect(() => {
    const initI18n = async () => {
      try {
        // On web, wait a bit to ensure fonts from CSS are loaded
        if (Platform.OS === 'web') {
          // Wait a minimal time for web fonts from CSS to initialize
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        await initializeI18n();
      } catch {
        // i18n initialization failed - continue anyway (will use default language)
      } finally {
        setIsI18nInitialized(true);
      }
    };
    initI18n();
  }, []);

  // When both fonts and i18n are ready, hide splash and trigger fade-in
  useEffect(() => {
    const prepareApp = async () => {
      if (fontsLoaded && isI18nInitialized && !appReady) {
        try {
          // Hide splash screen once fonts and i18n are ready
          await SplashScreen.hideAsync();
          
          // Small delay before fade-in for smoother transition
          await new Promise(resolve => setTimeout(resolve, 50));
          
          setAppReady(true);
          
          // Fade-in animation for smooth entry
          Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 300,
            useNativeDriver: true,
          }).start();
        } catch {
          // If splash screen hide fails, still proceed with app
          setAppReady(true);
          Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 300,
            useNativeDriver: true,
          }).start();
        }
      }
    };

    prepareApp();
  }, [fontsLoaded, isI18nInitialized, appReady, fadeAnim]);

  // Show loading screen while fonts and i18n are loading
  if (!fontsLoaded || !isI18nInitialized) {
    // Use i18n directly (not hook) since we're outside the provider, with Portuguese as fallback
    let loadingMessages: string[];
    try {
      loadingMessages = [
        i18n.t('loading.appInitialization.message1', 'Preparando tudo para você...'),
        i18n.t('loading.appInitialization.message2', 'Quase lá...'),
        i18n.t('loading.appInitialization.message3', 'Organizando suas ferramentas...'),
      ];
    } catch {
      // Fallback to Portuguese if i18n not available
      loadingMessages = [
        'Preparando tudo para você...',
        'Quase lá...',
        'Organizando suas ferramentas...',
      ];
    }
    const randomMessage = loadingMessages[Math.floor(Math.random() * loadingMessages.length)];
    
    return (
      <PaperProvider theme={currentTheme}>
        <SafeAreaProvider>
          <LoadingScreen 
            message={randomMessage}
            fullScreen={true}
          />
        </SafeAreaProvider>
      </PaperProvider>
    );
  }

  // Render app with fade-in animation for smooth entry.
  // ErrorBoundary is the outermost wrapper so a render crash anywhere in the
  // provider tree or any route surfaces a recoverable fallback (and a Sentry
  // event with user/company context) instead of a white screen.
  return (
    <ErrorBoundary boundary="root">
      <I18nextProvider i18n={i18n}>
        <PaperProvider theme={currentTheme}>
          <SafeAreaProvider>
            <Animated.View style={{ flex: 1, opacity: fadeAnim }}>
              <PreviewBanner />
              <OnboardingProvider>
                <StripeProvider>
                  <AuthProvider>
                    <CompanyProvider>
                      <QueryClientProvider client={queryClient}>
                        <StatusBar
                          barStyle={colorScheme === 'dark' ? 'light-content' : 'dark-content'}
                        />
                        <Stack
                          screenOptions={{
                            headerShown: false,
                            title: formattedTitle,
                          }}
                        />
                      </QueryClientProvider>
                    </CompanyProvider>
                  </AuthProvider>
                </StripeProvider>
              </OnboardingProvider>
              <ConsentBanner />
            </Animated.View>
          </SafeAreaProvider>
        </PaperProvider>
      </I18nextProvider>
    </ErrorBoundary>
  );
});